import { Button, type ButtonProps } from 'antd';
import { useRef, useEffect } from 'react';

/** Extiende Button de Ant Design; permite añadir onChange, htmlType, type, size, etc. */
interface ButtonPrimaryProps extends Omit<ButtonProps, 'onClick'> {
  onClick: () => void | Promise<void>;
  onClickWhenDisabled?: () => void;
}

const executingButtons = new Set<string>();
let globalId = 0;

const DISABLED_STYLES: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  cursor: 'not-allowed',
  backgroundColor: 'transparent',
  zIndex: 10
}

const ACTION_COOLDOWN_MS = 150;
const SUBMIT_COOLDOWN_MS = 500;

const ButtonPrimary = ({
  onClick,
  onClickWhenDisabled,
  disabled = false,
  loading = false,
  ...props
}: ButtonPrimaryProps) => {
  const buttonIdRef = useRef<string>(`btn-${globalId++}`);
  const buttonId = buttonIdRef.current;
  const isExecuting = executingButtons.has(buttonId);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      executingButtons.delete(buttonId);
    };
  }, [buttonId]);

  const handleClick = async () => {
    // CHEQUEO GLOBAL - si este botón ya está ejecutando, SALIR
    if (executingButtons.has(buttonId) || loading) return;

    const result = onClick();
    const promise = result != null && typeof (result as Promise<unknown>)?.then === 'function'
      ? (result as Promise<void>)
      : null;

    if (promise) {
      executingButtons.add(buttonId);
      const start = Date.now();
      try {
        await promise;
      } finally {
        // Acciones que acaban en < 150ms (ejemplo. abrir modal) no necesitan cooldown; evita botón
        // "pendiente" si el usuario cierra el modal. Submit/API se mantiene el cooldown de 500ms para evitar doble envío.
        const elapsed = Date.now() - start;
        const cooldownMs = elapsed < ACTION_COOLDOWN_MS ? 0 : SUBMIT_COOLDOWN_MS;
        setTimeout(() => {
          executingButtons.delete(buttonId);
        }, cooldownMs);
      }
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Solo ejecutar onClickWhenDisabled si está disabled y NO está ejecutando
    if (disabled && !isExecuting && !loading) {
      onClickWhenDisabled?.();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <Button
        {...props}
        className={props.className ?? 'button button-submit'}
        onClick={handleClick}
        disabled={disabled || isExecuting}
        loading={loading || isExecuting}
      >
        {props.children}
      </Button>
      
      {/* Capa SOLO cuando está disabled Y enableDisabledClick es true */}
      {onClickWhenDisabled && disabled && !isExecuting && !loading && (
        <div
          onClick={handleOverlayClick}
          style={DISABLED_STYLES}
        />
      )}
      
      {/* Capa cuando está ejecutando - solo bloquea, no es clickeable */}
      {(isExecuting || loading) && (
        <div
          style={{ ...DISABLED_STYLES, pointerEvents: 'all' }}
        />
      )}
    </div>
  );
};

export default ButtonPrimary;
